import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { CustomerStatus } from '@tenfour/contracts'
import {
  listCustomers,
  getCustomer,
  addCustomer,
  updateCustomer,
  setCustomerStatus,
  type CustomerAddressInput,
} from './api'

/**
 * The customer book. Default = ACTIVE customers only (the server's default,
 * ) — what every picker wants. The customers workspace asks for `'all'`
 * so its Inactive pill has rows to show.
 */
export function useCustomers(accountId: string, opts?: { status?: CustomerStatus | 'all' }) {
  const status = opts?.status
  return useQuery({
    queryKey: status ? ['customers', accountId, status] : ['customers', accountId],
    queryFn: () => listCustomers(accountId, status),
    enabled: !!accountId,
  })
}

/** Single customer detail (lazy) — only fetches when a customerId is provided. */
export function useCustomer(customerId: string | null | undefined) {
  return useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => getCustomer(customerId!),
    enabled: !!customerId,
  })
}

export function useAddCustomer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      accountId,
      data,
    }: {
      accountId: string
      data: CustomerAddressInput & { name: string; phone: string; email?: string; notes?: string; contactStatus?: string }
    }) => addCustomer(accountId, data),
    onSuccess: (_, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: ['customers', accountId] })
    },
  })
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      customerId,
      data,
    }: {
      customerId: string
      data: CustomerAddressInput & {
        name?: string
        phone?: string
        // Structured address (#287/#564) — the API recomposes the flat `address`.
        email?: string
        notes?: string
        contactStatus?: string
        doNotText?: boolean
        birthdayMonth?: number | null
        birthdayDay?: number | null
      }
    }) => updateCustomer(customerId, data),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      // The detail page caches the single row under its own key.
      queryClient.invalidateQueries({ queryKey: ['customer', customerId] })
    },
  })
}

/** Mark inactive / reactivate — the owner action that replaced delete. */
export function useSetCustomerStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ customerId, status }: { customerId: string; status: CustomerStatus }) =>
      setCustomerStatus(customerId, status),
    onSuccess: (_, { customerId }) => {
      // Every list variant (default ACTIVE + the workspace's 'all') and the
      // record page's own single-row cache.
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.invalidateQueries({ queryKey: ['customer', customerId] })
    },
  })
}
